import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, MenuItem, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import SlipPdfFrame from './slipPdfFrame';
import PdfBody from './slipPdfBody';
import { getIncentiveSlips, markSlipFulfilled, generateMonthlySlips } from '../../api/incentiveClaimApi';
import { approveIncentiveSlip, voidIncentiveSlip, exportIncentiveMakerCsv } from '../../api/incentiveSlipApi';
import { getAllCampaigns } from '../../api/incentiveApi';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { facetFilterOptions } from '../common/facetFilter';

const slipStatusCfg = {
    Approved: { bg: '#D1FAE5', color: '#065F46' },
    Pending: { bg: '#FEF3C7', color: '#92400E' },
    Voided: { bg: '#FEE2E2', color: '#991B1B' },
};

const rewardTypeCfg = {
    Cash: { bg: '#DBEAFE', color: '#1E3A8A' },
    Item: { bg: '#F3E8FF', color: '#6B21A8' },
    Travel: { bg: '#FCE7F3', color: '#9D174D' },
};

export default function IncentiveClaimSlipsList() {
    const [slips, setSlips] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignId, setCampaignId] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [viewSlip, setViewSlip] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        getAllCampaigns()
            .then(r => {
                const list = Array.isArray(r.data) ? r.data : [];
                setCampaigns(list);
                // Same fix as incentiveClaimsList: open on "All Campaigns" ('') rather than
                // jumping to the newest one. Generating slips still requires an explicit
                // pick — handleGenerate guards on !campaignId and the button is disabled
                // without one — so only the VIEW gets wider, never the write. (user 2026-08-28)
            })
            .catch(() => setCampaigns([]));
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getIncentiveSlips(campaignId);
            setSlips(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            console.error('Failed to load incentive slips:', e);
            setSlips([]);
        } finally {
            setLoading(false);
        }
    }, [campaignId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const filteredSlips = useMemo(() => {
        if (!typeFilter || typeFilter === 'All') return slips;
        if (typeFilter === 'Cash') return slips.filter(s => s.rewardType === 'Cash');
        return slips.filter(s => s.rewardType !== 'Cash');
    }, [slips, typeFilter]);

    const handleGenerate = async () => {
        if (!campaignId) { alert('Select a campaign first to generate its slips.'); return; }
        const camp = campaigns.find(c => String(c.campaignId ?? c.id) === String(campaignId));
        const name = camp ? (camp.name ?? camp.campaignName ?? `#${campaignId}`) : `#${campaignId}`;
        if (!window.confirm(`Generate monthly incentive slips for "${name}"?\n\nBundles every eligible agent's claims into Pending slips for approval. Regenerating replaces existing Pending slips.`)) return;
        setGenerating(true);
        try {
            const r = await generateMonthlySlips(campaignId);
            const created = r.data?.created ?? r.data?.Created ?? 0;
            alert(`Generated ${created} incentive slip(s) — Pending, submit for approval on the Slips page.`);
            fetchData();
        } catch (e) {
            alert('Failed to generate slips: ' + (e.response?.data || e.message));
        } finally {
            setGenerating(false);
        }
    };

    const handleApprove = async (slip) => {
        if (!window.confirm(`Approve this incentive slip for ${slip.agentName} (${handlePriceWithRM(slip.totalPayout)})?\n\nThis settles the bundled claims as paid.`)) return;
        try {
            await approveIncentiveSlip(slip.incentiveSlipId);
            setViewSlip(null);
            fetchData();
        } catch (e) {
            alert('Failed to approve: ' + (e.response?.data || e.message));
        }
    };

    const handleVoid = async (slip) => {
        const reason = window.prompt(`Void this incentive slip for ${slip.agentName}?\n\nReason:`, '');
        if (reason === null) return;
        try {
            await voidIncentiveSlip(slip.incentiveSlipId, reason);
            setViewSlip(null);
            fetchData();
        } catch (e) {
            alert('Failed to void: ' + (e.response?.data || e.message));
        }
    };

    // Approved + Cash slips are what the bank actually pays → Maker List CSV (mirrors the
    // commission maker export: grouped per agent → Billing Name / Bank / Account No / Amount).
    const makerEligible = useMemo(
        () => filteredSlips.filter(s => s.status === 'Approved' && s.rewardType === 'Cash'),
        [filteredSlips]
    );
    const handleExportMaker = async () => {
        const ids = makerEligible.map(s => s.incentiveSlipId);
        if (!ids.length) { alert('Maker CSV is only for Approved cash incentive slips — none in the current view.'); return; }
        try {
            const res = await exportIncentiveMakerCsv(ids);
            const data = res.data || [];
            if (!data.length) { alert('No data to export.'); return; }
            const headers = ['No', 'Billing Name', 'Bank Name', 'Bank Account No', 'Amount', 'Reference', 'Campaign', 'Remarks'];
            const escape = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
            const escapeAsText = (v) => `="${(v ?? '').toString().replace(/"/g, '""')}"`;  // keep long account no. verbatim in Excel
            const csvRows = data.map(d => [
                escape(d.no), escape(d.billingName), escape(d.bankName), escapeAsText(d.bankAccountNo),
                escape(d.amount), escape(d.reference), escape(d.campaign), escape(d.remarks),
            ]);
            const csv = '﻿' + [headers.map(escape).join(','), ...csvRows.map(r => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Incentive_Maker_List_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert('Export failed: ' + (e.response?.data || e.message));
        }
    };

    const handleMarkDelivered = async (slip) => {
        const remarks = window.prompt(`Delivery remarks for ${slip.agentName}'s reward (${slip.reward || slip.rewardType})?`, '');
        if (remarks === null) return;
        try {
            await markSlipFulfilled({ slipId: slip.incentiveSlipId, remarks });
            setViewSlip(null);
            fetchData();
        } catch (e) {
            alert('Failed to mark delivered: ' + (e.response?.data || e.message));
        }
    };

    const columns = useMemo(() => [
        { id: 'no', header: 'No', size: 50, enableColumnFilter: false, Cell: ({ row }) => row.index + 1 },
        { id: 'agentName', header: 'Agent', accessorKey: 'agentName', size: 150 },
        { id: 'campaignName', header: 'Campaign', accessorKey: 'campaignName', size: 160 },
        { id: 'period', header: 'Period', accessorKey: 'period', size: 120 },
        {
            id: 'rewardType', header: 'Reward Type', accessorKey: 'rewardType', size: 110,
            Cell: ({ cell }) => {
                const v = cell.getValue() || 'Cash';
                const cfg = rewardTypeCfg[v] || { bg: '#F1F5F9', color: '#64748B' };
                return <Chip label={v} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />;
            },
        },
        { id: 'type', header: 'Type', accessorFn: row => (row.rewardType === 'Cash' ? 'Cash' : 'Item'), size: 90, enableColumnFilter: false },
        {
            id: 'reward', header: 'Reward', size: 150,
            Cell: ({ row }) => {
                const s = row.original;
                // Cash -> RM amount; Item/Travel -> the item/travel name. Never assume cash.
                return s.rewardType === 'Cash'
                    ? handlePriceWithRM(s.totalPayout ?? s.rewardAmount ?? 0)
                    : (s.reward || '—');
            },
        },
        {
            id: 'statusPayout', header: 'Status / Payout', size: 170,
            Cell: ({ row }) => {
                const s = row.original;
                const cfg = slipStatusCfg[s.status] || slipStatusCfg.Pending;
                if (s.rewardType === 'Cash') {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Chip label={s.status} size="small" sx={{ height: 22, fontSize: '0.66rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />
                            <span style={{ fontSize: '0.75rem', color: s.status === 'Approved' ? '#065F46' : '#475569' }}>
                                {s.status === 'Approved' ? `Paid ${handlePriceWithRM(s.totalPayout)}` : handlePriceWithRM(s.totalPayout)}
                            </span>
                        </Box>
                    );
                }
                const fulfilled = s.fulfilmentStatus === 'Delivered';
                return (
                    <Chip
                        label={fulfilled ? 'Delivered' : 'Pending'}
                        size="small"
                        sx={{ height: 22, fontSize: '0.66rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: fulfilled ? '#D1FAE5' : '#FEF3C7', color: fulfilled ? '#065F46' : '#92400E' }}
                    />
                );
            },
        },
        {
            id: 'actions', header: '', size: 230, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => {
                const s = row.original;
                const isCash = s.rewardType === 'Cash';
                const isPending = s.status === 'Pending';
                const canDeliver = !isCash && s.status === 'Approved' && s.fulfilmentStatus !== 'Delivered';
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setViewSlip(s)}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2 }}
                        >
                            View
                        </Button>
                        {isPending && (
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => handleApprove(s)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2, boxShadow: 'none' }}
                            >
                                Approve
                            </Button>
                        )}
                        {isPending && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleVoid(s)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2 }}
                            >
                                Void
                            </Button>
                        )}
                        {canDeliver && (
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleMarkDelivered(s)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2, boxShadow: 'none', background: 'linear-gradient(135deg, #6366F1, #4F46E5)' }}
                            >
                                Mark Delivered
                            </Button>
                        )}
                    </Box>
                );
            },
        },
    ], []);

    const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
        columns,
        data: filteredSlips,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { isLoading: loading },
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
    });


    return (
        <div>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label="Campaign"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    sx={{ minWidth: 220, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Campaigns</MenuItem>
                    {campaigns.map(c => {
                        const id = c.campaignId ?? c.id;
                        return <MenuItem key={id} value={id}>{c.name ?? c.campaignName ?? `#${id}`}</MenuItem>;
                    })}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Type"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    sx={{ minWidth: 130, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Item">Item</MenuItem>
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportMaker}
                    disabled={makerEligible.length === 0}
                    sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', color: '#0F62CC', borderColor: '#1A8CFF' }}
                >
                    Export Maker CSV ({makerEligible.length})
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerate}
                    disabled={generating || !campaignId}
                    sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}
                >
                    {generating ? 'Generating...' : 'Generate Monthly Slips'}
                </Button>
            </Box>

            <MaterialReactTable table={table} />

            <Dialog open={!!viewSlip} onClose={() => setViewSlip(null)} fullWidth maxWidth="lg">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'Poppins', sans-serif" }}>
                    Incentive Slip — {viewSlip?.agentName}
                    <IconButton size="small" onClick={() => setViewSlip(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ background: '#F8FAFC', p: 3 }}>
                    {viewSlip && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ boxShadow: '0 4px 24px rgba(15,23,42,0.08)' }}>
                                <SlipPdfFrame
                                    title="Incentive Slip"
                                    agentName={viewSlip.agentName}
                                    period={viewSlip.period}
                                    dateIssued={viewSlip.dateIssued}
                                    totalPayout={viewSlip.totalPayout}
                                    status={viewSlip.status}
                                    sectionTitle="Incentive Achievement"
                                >
                                    <PdfBody slip={{ ...viewSlip, type: 'Incentive' }} />
                                </SlipPdfFrame>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button onClick={() => setViewSlip(null)} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>Close</Button>
                    {viewSlip && viewSlip.status === 'Pending' && (
                        <Button onClick={() => handleApprove(viewSlip)} variant="contained" color="success" sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', boxShadow: 'none' }}>
                            Approve
                        </Button>
                    )}
                    {viewSlip && viewSlip.rewardType !== 'Cash' && viewSlip.status !== 'Voided' && viewSlip.fulfilmentStatus !== 'Delivered' && (
                        <Button onClick={() => handleMarkDelivered(viewSlip)} variant="contained" sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg, #6366F1, #4F46E5)' }}>
                            Mark Delivered
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
}
